import { useToastContext } from "hooks/useToastContext";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import intl from "react-intl-universal";
import {
  searchFamily,
  searchLabeledFamily,
  searchMaterials,
  searchSuppliers,
  searchSupplierUsers,
} from "services/filters.service";
import {
  deleteProductionPlanSupplierHeader,
  getProductionPlanSuppliers,
  updateProductionPlanSupplierDay,
  updateProductionPlanSupplierHeader,
} from "services/production-plan-supplier.service";
import { formatErrorMessage } from "utils/handle-error";
import StorePersist from "utils/store-persist";
import { getUserId, isUserSupplier } from "utils/user";
import type {
  IProductionPlanSupplier,
  ProductionPlanSupplierContextProps,
  ProductionPlanSupplierFieldUpdate,
  ProductionPlanSupplierFilters,
  ProductionPlanSupplierOption,
  ProductionPlanSupplierPagination,
  ProductionPlanSupplierProviderProps,
} from "./types";
import {
  applyProductionPlanSupplierMutationResponse,
  getProductionPlanSupplierFiltersValidationError,
  getProductionPlanSupplierUseFilterDefault,
  normalizeProductionPlanSupplierFilters,
  normalizeProductionPlanSupplierOptions,
  productionPlanSupplierStorageKey,
} from "./utils";

const ProductionPlanSupplierContext = createContext<ProductionPlanSupplierContextProps | undefined>(
  undefined
);

export const getSupplierUserGroup = (
  supplierGroups: ProductionPlanSupplierOption[],
  filters: ProductionPlanSupplierFilters
) => {
  if (!isUserSupplier()) {
    return filters.SupplierGroups;
  }

  const userId = getUserId();
  const userGroup = supplierGroups.find(
    (supplierGroup) => String(supplierGroup.Id) === String(userId)
  );

  if (!userGroup) {
    return filters.SupplierGroups;
  }

  const hasUserGroupSelected = filters.SupplierGroups.some(
    (supplierGroup) => String(supplierGroup.Id) === String(userId)
  );

  return hasUserGroupSelected ? filters.SupplierGroups : [userGroup];
};

const ProductionPlanSupplierProvider = ({ children }: ProductionPlanSupplierProviderProps) => {
  const toastContext = useToastContext();
  const openToast = toastContext?.openToast ?? (() => undefined);
  const [suppliers, setSuppliers] = useState<IProductionPlanSupplier[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(0);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [filters, setFilters] = useState<ProductionPlanSupplierFilters>(() =>
    normalizeProductionPlanSupplierFilters(
      StorePersist.getValuesJSON(productionPlanSupplierStorageKey)
    )
  );
  const [pagination, setPagination] = useState<ProductionPlanSupplierPagination>(() => ({
    pageIndex: filters._page,
    pageSize: filters._size,
    totalItems: 0,
    totalPages: 0,
  }));
  const filtersRef = useRef(filters);
  const [supplierGroups, setSupplierGroups] = useState<ProductionPlanSupplierOption[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<ProductionPlanSupplierOption[]>([]);
  const [materialOptions, setMaterialOptions] = useState<ProductionPlanSupplierOption[]>([]);
  const [familyOptions, setFamilyOptions] = useState<ProductionPlanSupplierOption[]>([]);
  const [labeledFamilyOptions, setLabeledFamilyOptions] = useState<ProductionPlanSupplierOption[]>([]);

  const startLoading = useCallback(() => {
    setLoadingRequests((current) => current + 1);
  }, []);

  const stopLoading = useCallback(() => {
    setLoadingRequests((current) => Math.max(current - 1, 0));
  }, []);

  const loading = loadingRequests > 0;

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const persistFilters = useCallback((nextFilters: ProductionPlanSupplierFilters) => {
    StorePersist.setValuesJSON(productionPlanSupplierStorageKey, nextFilters);
  }, []);

  const fetchProductionPlanData = useCallback(
    async (nextFilters: ProductionPlanSupplierFilters) => {
      const response = await getProductionPlanSuppliers(nextFilters);
      setSuppliers(response.Data);
      setPagination({
        pageIndex: Math.max(response.CurrentPage, 0),
        pageSize: nextFilters._size,
        totalItems: response.TotalItems,
        totalPages: response.TotalPages,
      });
      return response;
    },
    []
  );

  const handleChangeFilter = useCallback(function handleChangeFilter<
    T extends keyof ProductionPlanSupplierFilters,
  >(prop: T, value: ProductionPlanSupplierFilters[T]) {
    setFilters((current) => ({
      ...current,
      [prop]: value,
    }));
  }, []);

  const searchProductionPlanData = useCallback(
    async (nextFilters = filtersRef.current) => {
      const normalizedFilters = normalizeProductionPlanSupplierFilters(nextFilters);
      const validationError = getProductionPlanSupplierFiltersValidationError(normalizedFilters);

      if (validationError) {
        openToast(validationError, "negative");
        return false;
      }

      startLoading();
      setFilters(normalizedFilters);
      persistFilters(normalizedFilters);

      try {
        await fetchProductionPlanData(normalizedFilters);
        return true;
      } catch (error) {
        openToast(formatErrorMessage(error), "negative");
        return false;
      } finally {
        stopLoading();
      }
    },
    [fetchProductionPlanData, openToast, persistFilters, startLoading, stopLoading]
  );

  const loadFilterOptions = useCallback(async () => {
    setFilterOptionsLoading(true);

    try {
      const [supplierGroupsData, suppliersData, materialsData, familiesData, labeledFamiliesData] = await Promise.all([
        searchSupplierUsers(),
        searchSuppliers(getProductionPlanSupplierUseFilterDefault()),
        searchMaterials(getProductionPlanSupplierUseFilterDefault()),
        searchFamily(getProductionPlanSupplierUseFilterDefault(), ""),
        searchLabeledFamily(getProductionPlanSupplierUseFilterDefault()),
      ]);

      const normalizedSupplierGroups = normalizeProductionPlanSupplierOptions(supplierGroupsData);
      const normalizedSuppliers = normalizeProductionPlanSupplierOptions(suppliersData);
      const normalizedMaterials = normalizeProductionPlanSupplierOptions(materialsData);
      const normalizedFamilies = normalizeProductionPlanSupplierOptions(familiesData);
      const normalizedLabeledFamilies = normalizeProductionPlanSupplierOptions(labeledFamiliesData);

      setSupplierGroups(normalizedSupplierGroups);
      setSupplierOptions(normalizedSuppliers);
      setMaterialOptions(normalizedMaterials);
      setFamilyOptions(normalizedFamilies);
      setLabeledFamilyOptions(normalizedLabeledFamilies);

      return {
        supplierGroups: normalizedSupplierGroups,
      };
    } catch (error) {
      openToast(formatErrorMessage(error), "negative");
      return {
        supplierGroups: [] as ProductionPlanSupplierOption[],
      };
    } finally {
      setFilterOptionsLoading(false);
    }
  }, [openToast]);

  useEffect(() => {
    const initialize = async () => {
      const options = await loadFilterOptions();
      const storedFilters = normalizeProductionPlanSupplierFilters(
        StorePersist.getValuesJSON(productionPlanSupplierStorageKey)
      );
      const nextFilters = {
        ...storedFilters,
        SupplierGroups: getSupplierUserGroup(options.supplierGroups, storedFilters),
      };

      await searchProductionPlanData(nextFilters);
    };

    void initialize();
  }, [loadFilterOptions, searchProductionPlanData]);

  const handleUpdateSupplierField = useCallback(
    async (supplierId: number, update: ProductionPlanSupplierFieldUpdate) => {
      const currentSupplier = suppliers.find((supplier) => supplier.Id === supplierId);

      if (!currentSupplier) {
        return;
      }

      startLoading();
      try {
        const response =
          update.field === "ProductionQuantity"
            ? await updateProductionPlanSupplierDay(supplierId, {
                productionDate: update.productionDate ?? "",
                productionQuantity: update.value,
                useFilterDefault: getProductionPlanSupplierUseFilterDefault(),
              })
            : await updateProductionPlanSupplierHeader(supplierId, {
                currentStock:
                  update.field === "CurrentStock"
                    ? (update.value ?? currentSupplier.CurrentStock)
                    : currentSupplier.CurrentStock,
                forecast: update.field === "Forecast" ? update.value : currentSupplier.Forecast,
                useFilterDefault: getProductionPlanSupplierUseFilterDefault(),
              });

        setSuppliers((currentSuppliers) =>
          applyProductionPlanSupplierMutationResponse(currentSuppliers, response)
        );
      } catch (error) {
        openToast(formatErrorMessage(error), "negative");
        throw error;
      } finally {
        stopLoading();
      }
    },
    [openToast, startLoading, stopLoading, suppliers]
  );

  const handleDeleteSelectedRows = useCallback(
    async (selectedRows: IProductionPlanSupplier[]) => {
      if (!selectedRows.length) {
        return;
      }

      startLoading();

      try {
        for (const selectedRow of selectedRows) {
          await deleteProductionPlanSupplierHeader(
            selectedRow.Id,
            getProductionPlanSupplierUseFilterDefault()
          );
        }
        await fetchProductionPlanData(filtersRef.current);
        openToast(intl.get("productionPlanSupplier.deleteModal.success"), "positive");
      } catch (error) {
        openToast(formatErrorMessage(error), "negative");
        throw error;
      } finally {
        stopLoading();
      }
    },
    [fetchProductionPlanData, openToast, startLoading, stopLoading]
  );

  const values = useMemo(
    () => ({
      suppliers,
      loading,
      filterOptionsLoading,
      filters,
      pagination,
      supplierGroups,
      supplierOptions,
      materialOptions,
      familyOptions,
      labeledFamilyOptions,
      handleChangeFilter,
      startLoading,
      stopLoading,
      searchProductionPlanData,
      handleUpdateSupplierField,
      handleDeleteSelectedRows,
    }),
    [
      familyOptions,
      labeledFamilyOptions,
      filterOptionsLoading,
      filters,
      handleChangeFilter,
      handleDeleteSelectedRows,
      handleUpdateSupplierField,
      loading,
      materialOptions,
      pagination,
      searchProductionPlanData,
      startLoading,
      stopLoading,
      supplierGroups,
      supplierOptions,
      suppliers,
    ]
  );

  return (
    <ProductionPlanSupplierContext.Provider value={values}>
      {children}
    </ProductionPlanSupplierContext.Provider>
  );
};

const useProductionPlanSupplierContext = () => {
  const context = useContext(ProductionPlanSupplierContext);

  if (!context)
    throw new Error(
      "useProductionPlanSupplierContext must be used within a ProductionPlanSupplierProvider"
    );
  return context;
};

export {
  ProductionPlanSupplierContext,
  ProductionPlanSupplierProvider,
  useProductionPlanSupplierContext,
};
